<template>
  <div class="workspace-update-panel">
    <WorkspaceSourcePanel :workspace-id="workspaceId"/>

    <template v-if="lastSyncWasAppRef">
      <div class="alert alert-success alert-sm">
        <CheckIcon/> This tree was last updated using the Sync app.
      </div>
      <button type="button" v-if="!busyRef" class="btn btn-inline btn-link upload-link" @click="onUploadClick">
        Upload a newer version of this file
      </button>
    </template>
    <template v-else>
      <div class="alert alert-primary alert-sm">
        This tree was uploaded directly from your browser. <router-link to="/learn/sync">Try the Sync app</router-link>
      </div>
      <button type="button" v-if="!busyRef" class="btn btn-inline btn-link upload-link" @click="onUploadClick">
        Upload a newer version
      </button>
    </template>

    <input ref="fileInputRef" type="file" :accept="acceptRef" class="visually-hidden" @change="onFileSelected"/>

    <div v-if="busyRef" class="progress-area">
      <ProgressBar :value="progressRef" smooth/>
      <div class="status">{{ statusTextRef }}</div>
    </div>

    <Modal ref="wrongTypeModalRef" info>
      <template #title>Wrong file type</template>
      This tree can only be updated using a {{ expectedTypeInfoRef?.product }} file.
    </Modal>

    <Modal ref="differentNameModalRef" @ok="confirmDifferentName.confirm" @hidden="confirmDifferentName.dismiss">
      <template #title>Different file name</template>
      <p>The file you selected has a different name than the source file for this tree. Use it to update your tree anyway?</p>
      <label class="form-label update-source">
        <input type="checkbox" class="form-check-input" v-model="updateSourceRef"/>
        Remember this as the new file name
      </label>
      <template #okText>Yes, use this file</template>
    </Modal>

    <Modal ref="unchangedModalRef" info>
      <template #title>No changes</template>
      This file hasn't changed since your last update.
    </Modal>

    <Modal ref="reviewModalRef" :ok-style="reviewOkStyleRef" @ok="confirmReview.confirm" @hidden="confirmReview.dismiss">
      <template #title>{{ reviewTitleRef }}</template>
      <SyncComparison v-if="comparisonRef" :comparison="comparisonRef" :last-person-count="lastPersonCountRef"/>
      <p class="review-prompt">Updating replaces your online tree with this file. Continue only if this is the right file.</p>
      <template #okText>{{ reviewOkTextRef }}</template>
    </Modal>

    <Modal ref="failedModalRef" info>
      <template #title>Something went wrong</template>
      We couldn't update your tree. Please try again.
    </Modal>

    <Modal ref="useSyncAppModalRef" class="use-sync-app-modal" @ok="onUploadConfirmed" @secondary-action="showSyncHelp">
      <template #title>Use the Sync app?</template>
      <p>The Sync app makes it easier to update your tree over time.</p>
      <p>Do you still want to upload your file directly?</p>
      <template #secondaryText>Learn about the Sync app</template>
      <template #okText>Upload my file</template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.workspace-update-panel {
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 1rem;
}

.alert {
  margin: 0;
  align-self: stretch;
}

.upload-link {
  font-size: 0.875rem;
}

.progress-area {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .status {
    font-size: 0.875rem;
    color: #888;
  }
}

.review-prompt {
  margin-top: 1rem;
}

.update-source {
  margin-top: 1rem;
  font-size: 1rem;
  color: #333;
}
</style>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useSyncSessionStore } from './SyncSessionStore'
import { SourceState, SyncScope, WorkspaceComparison, WorkspaceSimilarity } from './SyncSessionModel'
import { ItemType, SyncState, workspaceTypeInfos } from '@/rd/ResearchDataModel'
import { useWorkspaceStore } from '@/rd/WorkspaceStore'
import { useSyncStore } from '@/rd/SyncStore'
import { useResearchDataNotifications } from '@/rd/ResearchDataNotifications'
import { useTelemetry } from '@webapp/util/Telemetry'
import { useConfirmation } from '@webapp/util/Confirmation'
import { FileUtil } from '@webapp/util/FileUtil'
import { LoadMode } from '@webapp/util/AsyncData'
import { PatchChange } from '@webapp/util/Api'
import { CheckIcon } from '@zhuowenli/vue-feather-icons'
import WorkspaceSourcePanel from './WorkspaceSourcePanel.vue'
import Modal from '@webapp/util/Modal.vue'
import ProgressBar from '@webapp/util/ProgressBar.vue'
import SyncComparison from './SyncComparison.vue'
import { hideModalAsync, unexpectedErrorEvent } from '@/util/AppUtil'

const props = defineProps<{
  workspaceId: string
}>()

const router = useRouter()

const syncSessionStore = useSyncSessionStore()
const workspaceStore = useWorkspaceStore()
const syncStore = useSyncStore()
const notifications = useResearchDataNotifications()
const { trackException } = useTelemetry()

const fileInputRef = ref<HTMLInputElement>()
const useSyncAppModalRef = ref<InstanceType<typeof Modal>>()
const wrongTypeModalRef = ref<InstanceType<typeof Modal>>()
const differentNameModalRef = ref<InstanceType<typeof Modal>>()
const unchangedModalRef = ref<InstanceType<typeof Modal>>()
const reviewModalRef = ref<InstanceType<typeof Modal>>()
const failedModalRef = ref<InstanceType<typeof Modal>>()
const confirmDifferentName = useConfirmation(differentNameModalRef)
const confirmReview = useConfirmation(reviewModalRef)

const busyRef = ref(false)
const phaseRef = ref<'uploading' | 'syncing'>('uploading')
// Progress is a single 0..1 fraction so the bar only ever advances -- the upload fills the first
// portion, the server sync the rest, and completion tops it off.
const UPLOAD_PORTION = 0.5
// Hold the finished bar at 100% briefly so a fast (local) sync reads as done rather than a flash.
const COMPLETE_HOLD_MS = 1000
const progressRef = ref(0)
const comparisonRef = ref<WorkspaceComparison>()
const reviewSeverityRef = ref<WorkspaceSimilarity>()
const trackSyncIdRef = ref<string>()
const resyncSessionIdRef = ref<string>()
// Set (via the confirm checkbox) when the user opts to adopt a differently-named upload as the
// tree's source file going forward. Reset at the start of each resync so it can't go stale.
const updateSourceRef = ref(false)

const workspaceRef = computed(() => workspaceStore.getAsyncWorkspace(props.workspaceId, LoadMode.EnsureLoaded)?.data)
const lastSyncRef = computed(() => syncStore.getAsyncSync(workspaceRef.value?.lastSyncId, LoadMode.EnsureLoaded)?.data)
const expectedTypeInfoRef = computed(() => workspaceRef.value?.workspaceTypeInfo)
// The workspace's dataFilePath is the authoritative source-file identity (client-maintained). A
// re-sync is compared against it -- a mismatch means a different file than this tree's source.
const sourceFileNameRef = computed(() => FileUtil.fileName(workspaceRef.value?.dataFilePath))
const lastPersonCountRef = computed(() => lastSyncRef.value?.sourceDetails?.itemCounts?.[ItemType.Person]?.total)
const acceptRef = computed(() => expectedTypeInfoRef.value?.fileExtensions.join(',') ?? '')

// The last sync came from the desktop app if its agent isn't the web agent.
const lastSyncWasAppRef = computed(() => {
  const agent = lastSyncRef.value?.syncAgent
  return !!agent && agent !== 'Web'
})

const statusTextRef = computed(() => phaseRef.value === 'uploading' ? 'Uploading a new version...' : 'Updating your tree...')
const reviewIsAlarmingRef = computed(() => reviewSeverityRef.value === 'low')
const reviewOkStyleRef = computed(() => reviewIsAlarmingRef.value ? 'danger' as const : undefined)
const reviewTitleRef = computed(() => reviewIsAlarmingRef.value ? 'This looks like a different tree' : 'Update this tree?')
const reviewOkTextRef = computed(() => reviewIsAlarmingRef.value ? 'Yes, replace it anyway' : 'Update tree')

function onUploadClick() {
  // App-synced trees get a heads-up that the desktop app is the easier path.
  if (lastSyncWasAppRef.value) {
    useSyncAppModalRef.value?.open()
    return
  }
  fileInputRef.value?.click()
}

function onUploadConfirmed() {
  // Open the picker synchronously here -- doing it after an awaited modal loses the user gesture.
  fileInputRef.value?.click()
  useSyncAppModalRef.value?.close()
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = '' // let the same file be re-picked after a rejection
  if (file) {
    startResync(file)
  }
}

function typeInfoForName(name: string) {
  const ext = FileUtil.extension(name)
  return ext ? workspaceTypeInfos.find(t => t.fileExtensions.includes(ext)) : undefined
}

function isDifferentName(name: string) {
  const expected = sourceFileNameRef.value
  return !!(expected && name.toLowerCase() !== expected.toLowerCase())
}

function isUnchangedFromLastSync(file: File) {
  const source = lastSyncRef.value?.source
  return !!source && source.dataFileSize === file.size && source.modifiedDate?.toMillis() === file.lastModified
}

function hasFileChanged() {
  const lastHash = lastSyncRef.value?.source?.dataFileHash
  const currentHash = syncSessionStore.getSession(resyncSessionIdRef.value)?.sourceFileHash
  return !lastHash || !currentHash || lastHash !== currentHash
}

async function startResync(file: File) {
  updateSourceRef.value = false
  // Same size and modified time as the last sync -- almost certainly the same file, so don't upload.
  if (isUnchangedFromLastSync(file)) {
    unchangedModalRef.value?.open()
    return
  }
  // A new version must be the same file type -- a different program's file can't update this tree.
  const expected = expectedTypeInfoRef.value
  if (expected && typeInfoForName(file.name)?.id !== expected.id) {
    wrongTypeModalRef.value?.open()
    return
  }
  // A different name than this tree's source file is suspicious -- confirm before replacing the
  // tree. The confirm's checkbox (updateSourceRef) records whether to adopt it as the source.
  if (isDifferentName(file.name) && !(await confirmDifferentName.ask())) {
    return
  }

  busyRef.value = true
  phaseRef.value = 'uploading'
  progressRef.value = 0
  try {
    const sessionId = await syncSessionStore.createSessionAsync()
    resyncSessionIdRef.value = sessionId
    await syncSessionStore.uploadFileAsync(sessionId, file, (loaded, total) => {
      progressRef.value = total ? (loaded / total) * UPLOAD_PORTION : 0
    })
    await syncSessionStore.refreshSessionAsync(sessionId)
    if (syncSessionStore.getSession(sessionId)?.sourceState !== SourceState.Ready) {
      await failAndReset()
      return
    }
    // A re-saved file can be byte-identical despite a new timestamp -- the hash is the last word.
    if (!hasFileChanged()) {
      await syncSessionStore.abandonSessionAsync(sessionId)
      busyRef.value = false
      unchangedModalRef.value?.open()
      return
    }

    await syncSessionStore.loadSourceDetailsAsync(sessionId, new SyncScope())
    await syncSessionStore.loadLivingWindowGraphAsync(sessionId)

    // The similarity check is the safety gate against a destructive replace -- if it can't run, stop
    // and surface the error rather than syncing blind.
    try {
      comparisonRef.value = await syncSessionStore.compareToWorkspaceAsync(sessionId, props.workspaceId)
    }
    catch (error) {
      await syncSessionStore.abandonSessionAsync(sessionId)
      busyRef.value = false
      unexpectedErrorEvent.trigger({ error })
      return
    }
    const similarity = comparisonRef.value?.similarityTo(lastPersonCountRef.value)
    if ((similarity === 'low' || similarity === 'medium')) {
      reviewSeverityRef.value = similarity
      if (!(await confirmReview.ask())) {
        await syncSessionStore.abandonSessionAsync(sessionId)
        busyRef.value = false
        return
      }
    }

    // Keep the bar where the upload left it -- the sync continues filling from there.
    phaseRef.value = 'syncing'
    const { syncId } = await syncSessionStore.resyncWorkspaceAsync(sessionId, props.workspaceId)
    trackSyncIdRef.value = syncId
    // Completion is driven by the sync-state watch below.
  }
  catch (error) {
    trackException(error)
    await failAndReset()
  }
}

async function failAndReset() {
  await syncSessionStore.abandonSessionAsync(resyncSessionIdRef.value)
  resyncSessionIdRef.value = undefined
  busyRef.value = false
  failedModalRef.value?.open()
}

notifications.onSyncProgress(args => {
  if (args.syncID === trackSyncIdRef.value && args.total) {
    progressRef.value = UPLOAD_PORTION + (args.completed / args.total) * (1 - UPLOAD_PORTION)
  }
})

notifications.onSyncStateChanged(args => {
  if (args.itemId === trackSyncIdRef.value) {
    syncStore.getAsyncSync(trackSyncIdRef.value, LoadMode.Reload)
  }
})

const trackedSyncStateRef = computed(() =>
  trackSyncIdRef.value ? syncStore.getAsyncSync(trackSyncIdRef.value, LoadMode.TrackOnly)?.data?.state : undefined)

watch(trackedSyncStateRef, async (state) => {
  if (state === SyncState.Completed) {
    await onSyncCompleted()
  }
  else if (state === SyncState.Failed || state === SyncState.Cancelled) {
    onSyncFailed()
  }
})

async function onSyncCompleted() {
  // If the user opted to adopt this file as the source, take its name from the session before the
  // session is torn down below.
  const newSourceFileName = updateSourceRef.value ? syncSessionStore.getSession(resyncSessionIdRef.value)?.sourceFilePath : undefined
  await syncSessionStore.destroyCompletedSessionAsync(resyncSessionIdRef.value)
  resyncSessionIdRef.value = undefined
  trackSyncIdRef.value = undefined
  // Adopt the new source file: its name, and the device (OS) it came from. Best-effort -- the
  // workspace owns the source identity, but a failure here shouldn't disrupt the completed sync.
  if (newSourceFileName) {
    try {
      await workspaceStore.patchAsync(props.workspaceId, [
        new PatchChange('/dataFilePath', newSourceFileName),
        new PatchChange('/clientDeviceInfo', syncSessionStore.buildClientDeviceInfo()),
      ])
    }
    catch (error) {
      trackException(error)
    }
  }
  // Fill the bar to 100% and hold briefly so a fast (local) sync reads as done, not a flash.
  progressRef.value = 1
  await new Promise(resolve => setTimeout(resolve, COMPLETE_HOLD_MS))
  busyRef.value = false
  // Reload the workspace so the panel's file/device/date reflect the new sync.
  workspaceStore.getAsyncWorkspace(props.workspaceId, LoadMode.Reload)
}

function onSyncFailed() {
  syncSessionStore.destroyCompletedSessionAsync(resyncSessionIdRef.value)
  resyncSessionIdRef.value = undefined
  trackSyncIdRef.value = undefined
  busyRef.value = false
  failedModalRef.value?.open()
}

async function showSyncHelp() {
  await hideModalAsync(useSyncAppModalRef.value!.$el)
  router.push("/learn/sync")
}
</script>
